import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, startWith, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, NEVER, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Tutorial } from '../../models/tutorial';
import { HowToService } from '../../services/how-to.service';
import { VideoComponent } from '../video/video.component';
import { TutorialComponent } from '../tutorial/tutorial.component';
import { TutorialSkeletonComponent } from '../tutorial-skeleton/tutorial-skeleton.component';

@Component({
  selector: 'app-tutorial-list',
  templateUrl: './tutorial-list.component.html',
  styleUrls: ['./tutorial-list.component.scss'],
  imports: [TutorialComponent, TutorialSkeletonComponent],
  standalone: true
})
export class TutorialListComponent implements OnInit, OnDestroy {
  @Input() set selectedTag(value: string) {
    this.tag$.next(value);
  }

  @Input() set searchTerm(value: string) {
    this.searchTerm$.next(value);
  }

  searchTerm$ = new BehaviorSubject('');
  tag$ = new BehaviorSubject('');
  tutorials: Tutorial[] = [];
  state: 'loading' | 'ready' = 'loading';
  tutorialsBeingDownloaded: number[] = [];

  private subscription = new Subscription();

  constructor(
    private tutorialsService: HowToService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    const filterChanges$ = this.searchTerm$.pipe(debounceTime(500), distinctUntilChanged());
    const refresh$ = this.tutorialsService.listRefreshed$().pipe(startWith(null));
    this.subscription.add(
      combineLatest([filterChanges$, this.tag$, refresh$])
        .pipe(
          tap(() => (this.state = 'loading')),
          switchMap(([searchTerm, tag]) =>
            this.tutorialsService.getTutorials({
              searchTerm,
              tag,
              sortColumn: 'name',
              sortOrder: 'asc'
            })
          ),
          tap(response => {
            this.tutorials = response.data;
            this.state = 'ready';
          }),
          catchError(err => {
            console.error(err);
            this.state = 'ready';
            return NEVER;
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onTutorialClick(tutorial: Tutorial, tutorialIndex: number) {
    if (tutorial.type === 'Pdf') {
      this.tutorialsBeingDownloaded.push(tutorialIndex);
      this.subscription.add(
        this.tutorialsService
          .downloadPdf(tutorial.id!)
          .pipe(tap(() => this.hideDownloadIndicator(tutorialIndex)))
          .subscribe()
      );
    } else {
      this.dialog.open(VideoComponent, {
        height: '720px',
        width: '1280px',
        data: { url: tutorial.url, name: tutorial.name }
      });
    }
  }

  private hideDownloadIndicator(tutorialIndex: number) {
    const index = this.tutorialsBeingDownloaded.indexOf(tutorialIndex);
    if (index !== -1) {
      this.tutorialsBeingDownloaded.splice(index, 1);
    }
  }
}
